<template>
  <div class="space-y-6">
    <!-- Global Error/Info Message -->
    <GlobalFormMessage
      v-if="globalMessage"
      :message="globalMessage.message"
      :type="globalMessage.type"
      :dismissible="globalMessage.dismissible"
      :auto-dismiss="globalMessage.autoDismiss"
      @dismiss="clearGlobalMessage"
    />

    <!-- Step 1: Company Selection (Always visible for new users) -->
    <div v-if="!props.user">
      <CompanySelector
        v-model="newUserForm.company"
        :error="newUserErrors.company"
        @change="handleCompanyChange"
      />

      <!-- Divider -->
      <div class="my-6 border-t border-gray-200"></div>
    </div>

    <!-- Step 2: User Type Selection (Disabled until company selected for new users) -->
    <div>
      <h3 class="text-lg font-medium mb-4">
        {{ props.user ? 'Change User Type' : 'Select User Type' }}
      </h3>
      <div class="space-y-3">
        <label class="block text-sm font-medium">
          User Type <span class="text-red-500">*</span>
        </label>
        <div class="grid grid-cols-4 gap-3">
          <button
            v-for="type in ['admin', 'agent', 'engineer', 'client']"
            :key="type"
            @click="selectUserType(type as UserType)"
            :disabled="!props.user && !newUserForm.company"
            :class="[
              'p-3 rounded-lg border-2 text-center transition-all flex flex-col items-center justify-center',
              (props.user ? editUserForm.userType : newUserForm.userType) === type
                ? getUserTypeBadgeClass(type as UserType) + ' border-current'
                : 'border-gray-200 hover:border-gray-300',
              !props.user && !newUserForm.company ? 'opacity-50 cursor-not-allowed' : '',
            ]"
          >
            <div class="mb-1 flex items-center justify-center">
              <component :is="getUserTypeIcon(type as UserType)" class="w-6 h-6" />
            </div>
            <div class="font-medium text-sm">{{ getUserTypeLabel(type as UserType) }}</div>
          </button>
        </div>
        <p v-if="(props.user ? editUserError.userType : newUserErrors.userType)" class="text-red-500 text-sm mt-1">
          {{ props.user ? editUserError.userType : newUserErrors.userType }}
        </p>
      </div>
    </div>

    <!-- Only show form for new users -->
    <div v-if="!props.user" class="flex flex-col gap-4">
      <!-- Basic Information -->
      <div class="flex flex-col gap-4">
        <h3 class="text-lg font-medium text-gray-900">Basic Information</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- First Name -->
          <TextInputDefault
            v-model="newUserForm.firstName"
            type="text"
            id="firstName"
            name="firstName"
            labelText="First Name"
            placeholder="Enter first name"
            :asterisk="true"
            :errorMessage="newUserErrors.firstName"
            :required="true"
            :disabled="!newUserForm.company"
            @update:modelValue="() => clearNewUserError('firstName')"
          />

          <!-- Last Name -->
          <TextInputDefault
            v-model="newUserForm.lastName"
            type="text"
            id="lastName"
            name="lastName"
            labelText="Last Name"
            placeholder="Enter last name"
            :asterisk="true"
            :errorMessage="newUserErrors.lastName"
            :required="true"
            :disabled="!newUserForm.company"
            @update:modelValue="() => clearNewUserError('lastName')"
          />

          <!-- Email -->
          <TextInputDefault
            v-model="newUserForm.email"
            type="email"
            id="email"
            name="email"
            labelText="Email"
            placeholder="Enter email address"
            :asterisk="true"
            :errorMessage="newUserErrors.email"
            :required="true"
            :disabled="!newUserForm.company"
            @update:modelValue="() => clearNewUserError('email')"
          />

          <!-- Phone -->
          <TextInputDefault
            v-model="newUserForm.phone"
            type="tel"
            id="phone"
            name="phone"
            labelText="Phone Number"
            placeholder="Enter 10-digit number"
            :asterisk="true"
            :errorMessage="newUserErrors.phone"
            :required="true"
            maxlength="10"
            :disabled="!newUserForm.company"
            @update:modelValue="handlePhoneInput"
          />
        </div>

        <!-- Auto-generated username display -->
        <div v-if="autoGeneratedUsername" class="p-3 bg-blue-50 border border-blue-200 rounded">
          <h4 class="text-sm font-medium text-blue-700 mb-1">Auto-generated Information:</h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <span class="text-blue-600">Username:</span>
              <span class="font-medium ml-2">{{ autoGeneratedUsername }}</span>
            </div>
            <div>
              <span class="text-blue-600">Password:</span>
              <span class="font-medium ml-2 text-green-600">Will be set via email invitation</span>
            </div>
          </div>
        </div>
      </div>

      <!-- User Instructions -->
      <div class="bg-yellow-50 border border-yellow-200 rounded p-3">
        <h4 class="text-sm font-medium text-yellow-700 mb-1">User Setup Process:</h4>
        <ul class="text-xs text-yellow-700 space-y-1">
          <li>• Username will be auto-generated as: <strong>{{ autoGeneratedUsername || 'firstname.lastname' }}</strong></li>
          <li>• User will receive an email invitation to set their password</li>
          <li>• Email will contain a secure link to complete account setup</li>
          <li>• User must set password within 24 hours of receiving the email</li>
        </ul>
      </div>
    </div>

    <!-- Current User Info (only for edit mode) -->
    <div v-if="props.user" class="bg-gray-50 p-4 rounded-lg">
      <h3 class="text-sm font-medium text-gray-700 mb-2">Current User Information:</h3>
      <div class="space-y-2 text-sm">
        <div class="grid grid-cols-2 gap-2">
          <div>
            <span class="text-gray-600">Company:</span>
            <CompanyBadge :company="props.user.company" class="ml-2" />
          </div>
          <div>
            <span class="text-gray-600">Name:</span>
            <span class="font-medium ml-2">{{ props.user.firstName }} {{ props.user.lastName }}</span>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-2">
          <div>
            <span class="text-gray-600">Email:</span>
            <span class="font-medium ml-2">{{ props.user.email }}</span>
          </div>
          <div>
            <span class="text-gray-600">Username:</span>
            <span class="font-medium ml-2">{{ props.user.username || 'N/A' }}</span>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-2">
          <div>
            <span class="text-gray-600">Phone:</span>
            <span class="font-medium ml-2">{{ props.user.phone || 'N/A' }}</span>
          </div>
          <div>
            <span class="text-gray-600">Created On:</span>
            <span class="font-medium ml-2">{{ formatDate(props.user.createdAt) }}</span>
          </div>
        </div>
        <div>
          <span class="text-gray-600">Status:</span>
          <Badge
            :value="props.user.status === 'active' ? 'Active' : 'Inactive'"
            :class="props.user.status === 'active'
              ? 'bg-green-100 text-green-800 border border-green-200'
              : 'bg-gray-100 text-gray-800 border border-gray-200'"
            class="ml-2 px-2 py-0.5 text-xs"
          />
        </div>
      </div>
    </div>

    <!-- Validation Summary -->
    <div v-if="filteredErrors.length > 0"
         class="p-3 bg-red-50 border border-red-200 rounded-lg">
      <p class="text-sm font-medium text-red-700 mb-1">Please fix the following errors:</p>
      <ul class="text-xs text-red-600 space-y-1">
        <li v-for="error in filteredErrors" :key="error.field">
          • {{ error.error }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, watch, computed, ref } from 'vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import Badge from 'primevue/badge'
import CompanySelector from '@/components/forms/CompanySelector.vue'
import CompanyBadge from '@/components/badges/CompanyBadge.vue'
import GlobalFormMessage from '@/components/forms/GlobalFormMessage.vue'
import type { User, UserFormData, CompanyType } from '@/types/user'
import type { UserType } from '@/utils/roleHandler'
import {
  AdminIcon,
  AgentIcon,
  EngineerIcon,
  UsersGroupIcon,
  UserCircleIcon,
} from '@/icons'

interface ApiError {
  message?: string
  response?: {
    status?: number
    data?: {
      field?: string
      message?: string
      errors?: Array<{
        field: string
        message: string
      }>
      email?: string
      phone?: string
      error?: string
      [key: string]: unknown
    }
  }
  [key: string]: unknown
}

const props = defineProps<{
  user: User | null
  loading: boolean
  apiError?: ApiError | null
}>()

// Form states - FIX: Initialize company as undefined instead of empty string
const newUserForm = reactive({
  company: undefined as CompanyType | undefined,
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  userType: 'agent' as UserType
})

const editUserForm = reactive({
  userType: 'agent' as UserType
})

// Error states
const newUserErrors = reactive({
  company: '',
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  userType: ''
})

const editUserError = reactive({
  userType: ''
})

// Global message
const globalMessage = ref<{
  message: string
  type: 'error' | 'warning' | 'info' | 'success' | 'loading'
  dismissible?: boolean
  autoDismiss?: number | false
} | null>(null)

// Auto-generated username
const autoGeneratedUsername = computed(() => {
  if (!newUserForm.firstName.trim() && !newUserForm.lastName.trim()) {
    return ''
  }

  const firstName = newUserForm.firstName.trim().toLowerCase()
  const lastName = newUserForm.lastName.trim().toLowerCase()

  if (firstName && lastName) {
    return `${firstName}.${lastName}`
  } else if (firstName) {
    return firstName
  } else if (lastName) {
    return lastName
  }

  return ''
})

// Helper functions
const clearAllErrors = (): void => {
  Object.keys(newUserErrors).forEach(key => {
    newUserErrors[key as keyof typeof newUserErrors] = ''
  })
  Object.keys(editUserError).forEach(key => {
    editUserError[key as keyof typeof editUserError] = ''
  })
  clearGlobalMessage()
}

const clearNewUserErrors = (): void => {
  Object.keys(newUserErrors).forEach(key => {
    newUserErrors[key as keyof typeof newUserErrors] = ''
  })
  clearGlobalMessage()
}

const clearEditUserError = (): void => {
  editUserError.userType = ''
  clearGlobalMessage()
}

const clearGlobalMessage = (): void => {
  globalMessage.value = null
}

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

const isValidPhone = (phone: string): boolean => {
  if (!phone.trim()) return false
  return /^\d{10}$/.test(phone)
}

const handlePhoneInput = (): void => {
  clearNewUserError('phone')
  const digitsOnly = newUserForm.phone.replace(/\D/g, '')
  newUserForm.phone = digitsOnly.slice(0, 10)
}

const handleCompanyChange = (company: string) => {
  // FIX: Handle both string and CompanyType
  if (company === 'maxstationary' || company === 'ostnasik' || company === 'shradhha-ent') {
    newUserForm.company = company as CompanyType
  } else {
    newUserForm.company = undefined
  }

  if (newUserForm.company) {
    newUserErrors.company = ''
  }
}

const selectUserType = (type: UserType) => {
  if (!props.user && !newUserForm.company) {
    newUserErrors.company = 'Please select a company first'
    return
  }

  if (props.user) {
    editUserForm.userType = type
  } else {
    newUserForm.userType = type
  }
}

// Parse API error
const parseApiError = (error: ApiError | null | undefined): void => {
  clearAllErrors()

  if (!error) return

  console.log('Parsing API error:', error)

  if (error.response?.data) {
    const responseData = error.response.data

    if (Array.isArray(responseData.errors)) {
      responseData.errors.forEach((err: { field: string; message: string }) => {
        if (err.field && err.message) {
          const field = err.field as keyof typeof newUserErrors
          if (field in newUserErrors) {
            newUserErrors[field] = err.message
          }
        }
      })
    }
    else if (responseData.field) {
      const field = responseData.field as keyof typeof newUserErrors
      if (field in newUserErrors) {
        newUserErrors[field] = responseData.message || 'This value is already in use'
      }
    }
    else if (responseData.message) {
      setGlobalMessage(String(responseData.message), 'error', true, 5000)
    }
  } else if (error.message) {
    setGlobalMessage(error.message, 'error', true, 5000)
  }
}

const setGlobalMessage = (
  message: string,
  type: 'error' | 'warning' | 'info' | 'success' | 'loading' = 'error',
  dismissible = true,
  autoDismiss: number | false = 5000
): void => {
  globalMessage.value = {
    message,
    type,
    dismissible,
    autoDismiss
  }
}

// Watch for API error changes
watch(() => props.apiError, (error) => {
  if (error) {
    parseApiError(error)
  } else {
    clearAllErrors()
  }
}, { immediate: true })

// Get user type badge class
const getUserTypeBadgeClass = (userType: UserType): string => {
  const classMap: Record<string, string> = {
    admin: 'bg-purple-100 text-purple-800 border border-purple-200',
    agent: 'bg-blue-100 text-blue-800 border border-blue-200',
    engineer: 'bg-green-100 text-green-800 border border-green-200',
    client: 'bg-orange-100 text-orange-800 border border-orange-200',
  }
  return classMap[userType] || 'bg-gray-100 text-gray-800 border border-gray-200'
}

const getUserTypeLabel = (userType: UserType): string => {
  const labelMap: Record<string, string> = {
    admin: 'Administrator',
    agent: 'Agent',
    engineer: 'Engineer',
    client: 'Client',
  }
  return labelMap[userType] || userType
}

const getUserTypeIcon = (userType: UserType) => {
  switch (userType) {
    case 'admin':
      return AdminIcon
    case 'agent':
      return AgentIcon
    case 'engineer':
      return EngineerIcon
    case 'client':
      return UserCircleIcon
    default:
      return UsersGroupIcon
  }
}

const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

const resetNewUserForm = (): void => {
  newUserForm.company = undefined
  newUserForm.email = ''
  newUserForm.firstName = ''
  newUserForm.lastName = ''
  newUserForm.phone = ''
  newUserForm.userType = 'agent'
}

const resetEditUserForm = (): void => {
  editUserForm.userType = 'agent'
}

// Watch for user prop changes
watch(() => props.user, (newUser) => {
  if (newUser) {
    editUserForm.userType = newUser.userType || 'agent'
  } else {
    resetNewUserForm()
  }
  clearAllErrors()
}, { immediate: true })

const filteredErrors = computed(() => {
  if (!props.user) {
    return Object.entries(newUserErrors)
      .filter(([, error]) => error)
      .map(([field, error]) => ({ field, error }))
  } else {
    return Object.entries(editUserError)
      .filter(([, error]) => error)
      .map(([field, error]) => ({ field, error }))
  }
})

const clearNewUserError = (field: keyof typeof newUserErrors): void => {
  newUserErrors[field] = ''
  clearGlobalMessage()
}

// Validation functions
const validateNewUserForm = (): boolean => {
  let isValid = true
  clearNewUserErrors()

  if (!newUserForm.company) {
    newUserErrors.company = 'Please select a company'
    isValid = false
  }

  if (!newUserForm.firstName.trim()) {
    newUserErrors.firstName = 'First name is required'
    isValid = false
  }

  if (!newUserForm.lastName.trim()) {
    newUserErrors.lastName = 'Last name is required'
    isValid = false
  }

  if (!newUserForm.email.trim()) {
    newUserErrors.email = 'Email is required'
    isValid = false
  } else if (!isValidEmail(newUserForm.email)) {
    newUserErrors.email = 'Please enter a valid email address'
    isValid = false
  }

  if (!newUserForm.phone.trim()) {
    newUserErrors.phone = 'Phone number is required'
    isValid = false
  } else if (!isValidPhone(newUserForm.phone)) {
    newUserErrors.phone = 'Phone number must be exactly 10 digits'
    isValid = false
  }

  if (!newUserForm.userType) {
    newUserErrors.userType = 'Please select a user type'
    isValid = false
  }

  return isValid
}

const validateEditUserForm = (): boolean => {
  let isValid = true
  clearEditUserError()

  if (!editUserForm.userType) {
    editUserError.userType = 'Please select a user type'
    isValid = false
  }

  return isValid
}

// Get form data for parent
const getFormData = (): Partial<UserFormData> | null => {
  let isValid = false
  const userData: Partial<UserFormData> = {}

  if (!props.user) {
    isValid = validateNewUserForm()
    if (isValid) {
      userData.company = newUserForm.company
      userData.username = autoGeneratedUsername.value
      userData.firstName = newUserForm.firstName
      userData.lastName = newUserForm.lastName
      userData.email = newUserForm.email
      userData.phone = newUserForm.phone
      userData.userType = newUserForm.userType
      return userData
    }
  } else {
    isValid = validateEditUserForm()
    if (isValid) {
      userData.userType = editUserForm.userType
      return userData
    }
  }

  return null
}

// Expose functions
defineExpose({
  getFormData,
  newUserForm,
  editUserForm,
  autoGeneratedUsername,
  validateNewUserForm,
  validateEditUserForm,
  resetNewUserForm,
  resetEditUserForm
})
</script>

<style scoped>
.bg-purple-100 { background-color: #f3e8ff; }
.bg-blue-100 { background-color: #dbeafe; }
.bg-green-100 { background-color: #dcfce7; }
.bg-orange-100 { background-color: #ffedd5; }
.bg-gray-100 { background-color: #f3f4f6; }

.text-purple-800 { color: #6b21a8; }
.text-blue-800 { color: #1e40af; }
.text-green-800 { color: #166534; }
.text-orange-800 { color: #9a3412; }
.text-gray-800 { color: #374151; }

.opacity-50 { opacity: 0.5; }
.cursor-not-allowed { cursor: not-allowed; }
</style>
